import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import ConfigCard from './ConfigCard';

/**
 * "Images" card from the Header/Logo modal — three image slots: logo,
 * background image, favicon. Each slot shows a preview when an image URL
 * is present, otherwise a placeholder + "Choose file" button.
 *
 * Reusable for any module that needs the same upload-with-preview pattern.
 *
 * value shape:
 *   { logoUrl, backgroundUrl, faviconUrl }
 *
 * Callbacks:
 *   onChange(field, url)   — emitted by the parent after a successful upload,
 *                            or with '' when the user removes an image.
 *   onUpload(field, file)  — fires when the user picks a file. Parent does the
 *                            actual upload and then calls onChange with the URL.
 */
const HeaderImagesCard = ({ value = {}, onChange, onUpload, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb = '') => (value[k] !== undefined ? value[k] : fb);

    return (
        <ConfigCard
            anchor="cfg-card-header-images"
            title={tr('cfg_header_images_title', 'Images')}
            description={tr('cfg_header_images_desc', 'Upload your logo, header background and favicon.')}
            theme={theme}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <ImageSlot
                    field="logoUrl"
                    label={tr('cfg_header_images_logo', 'Logo')}
                    helper={tr('cfg_header_images_logo_helper', 'Your shop logo. Appears in the header.')}
                    value={get('logoUrl')}
                    onChange={onChange}
                    onUpload={onUpload}
                    aspect="square"
                    tr={tr}
                />
                <ImageSlot
                    field="backgroundUrl"
                    label={tr('cfg_header_images_background', 'Background image')}
                    helper={tr('cfg_header_images_background_helper', 'Optional — stretches behind the header above page content.')}
                    value={get('backgroundUrl')}
                    onChange={onChange}
                    onUpload={onUpload}
                    aspect="square"
                    tr={tr}
                />
                <ImageSlot
                    field="faviconUrl"
                    label={tr('cfg_header_images_favicon', 'Favicon')}
                    helper={tr('cfg_header_images_favicon_helper', 'Used as the icon shown in browser tabs and bookmarks.')}
                    value={get('faviconUrl')}
                    onChange={onChange}
                    onUpload={onUpload}
                    aspect="square"
                    tr={tr}
                />
            </Box>
        </ConfigCard>
    );
};

const ImageSlot = ({ field, label, helper, value, onChange, onUpload, aspect, tr }) => {
    const inputRef = useRef(null);
    const handlePick = () => inputRef.current?.click();
    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (f && onUpload) onUpload(field, f);
        // Reset so the same file can be picked twice in a row.
        if (inputRef.current) inputRef.current.value = '';
    };
    const handleRemove = () => onChange && onChange(field, '');

    return (
        <Stack spacing={1}>
            {/* minHeight reserves room for two caption lines so the image
                previews below all start at the same vertical baseline,
                regardless of how short an individual helper text is. */}
            <Box sx={{ minHeight: 56, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block' }}>
                    {label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {helper}
                </Typography>
            </Box>
            <Box sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: aspect === 'wide' ? '16 / 9' : '1 / 1',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.02)',
                backgroundImage: value ? `url("${value}")` : undefined,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
            }}>
                {!value && <ImageIcon fontSize="large" sx={{ opacity: 0.4 }} />}
                {value && (
                    <IconButton
                        size="small"
                        onClick={handleRemove}
                        sx={{
                            position: 'absolute', top: 4, right: 4,
                            backgroundColor: 'rgba(255,255,255,0.85)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        }}
                        title={tr('cfg_header_images_remove', 'Remove image')}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handlePick}
                fullWidth
            >
                {value
                    ? tr('cfg_header_images_replace', 'Replace')
                    : tr('cfg_header_images_choose', 'Choose file')}
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                style={{ display: 'none' }}
            />
        </Stack>
    );
};

HeaderImagesCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    onUpload: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default HeaderImagesCard;
